import React from 'react';
import { LockIcon, UsersIcon, ShieldCheckIcon, ZapIcon, EyeIcon, MapPinIcon } from 'lucide-react';
import { Logo } from '../shell/Logo';

/**
 * The Campuzon authentication canvas.
 *
 * One composition serves every authentication screen, because signup, sign-in,
 * verification and recovery are the same product moment seen at different
 * points — giving each its own layout would make the front door feel like
 * several different products.
 *
 * The photograph is not decoration. It carries the brand statement and the
 * four trust points, which is why it holds half the frame on desktop and
 * becomes a real (if shorter) banner on mobile rather than disappearing.
 */

const HERO = 'https://cdn.magicpatterns.com/patterns/generated-images/fa5fb616-ff15-4f51-9de9-1e055645d49e.jpg';
const HERO_ALT = 'A student in a green Campuzon hoodie checking her phone on a sunlit university campus walkway.';
const VALUES = [{
  icon: LockIcon,
  title: 'One secure identity',
  text: 'Your data is protected and private.'
}, {
  icon: UsersIcon,
  title: 'Multiple experiences',
  text: 'Student, provider, community and more.'
}, {
  icon: ShieldCheckIcon,
  title: 'Verified & trusted',
  text: 'We keep our campus community safe.'
}, {
  icon: ZapIcon,
  title: 'Built for Africa',
  text: 'Designed for students and campuses like yours.'
}];
const ASSURANCES = [{
  icon: ShieldCheckIcon,
  title: 'Your privacy matters',
  text: 'We never sell your data. You control what each audience can see.'
}, {
  icon: MapPinIcon,
  title: 'Built for Nigeria',
  text: 'Designed for our campuses, and for the way they actually work.'
}, {
  icon: EyeIcon,
  title: 'Clear about what we know',
  text: 'Verified, entered by you, or not checked — always labelled.'
}];
interface AuthSplitProps {
  /** The three-line brand statement over the photograph. */
  headline?: [string, string, string];
  lead?: string;
  /** Shown top-right of the photograph. */
  badge?: string;
  /** The authentication surface itself. */
  children: React.ReactNode;
  /** Secondary link sitting above the card, e.g. “Already have an account?”. */
  aside?: React.ReactNode;
}
export function AuthSplit({
  headline = ['Your campus.', 'Your journey.', 'One account.'],
  lead = 'Join students, campuses, providers and communities building the future of education together.',
  badge = 'One account. Everywhere on campus.',
  children,
  aside
}: AuthSplitProps) {
  return <div className="min-h-screen bg-canvas px-0 py-0 lg:px-6 lg:py-6" data-id="element-539" data-mp="dvk7cp85ra0h">
      <div className="mx-auto w-full max-w-[1440px] overflow-hidden bg-surface shadow-[0_24px_60px_-30px_rgba(15,32,25,0.35)] lg:rounded-3xl lg:border lg:border-line" data-id="element-540" data-mp="in3eep0orc6g">
        <div className="grid lg:grid-cols-2" data-id="element-541" data-mp="noj08jaotmpw">
          {/* ------------------------------------------------ visual panel */}
          <aside className="relative isolate min-h-[340px] overflow-hidden sm:min-h-[420px] lg:min-h-[760px]" data-id="element-542" data-mp="xkcjeliwwegn">
            <img src={HERO} alt={HERO_ALT} fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover object-[62%_28%]" data-id="element-543" data-mp="2i36hdlyrgcs5" />
            {/* Readability, not styling: the headline sits on photography. */}
            <div aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,32,23,0.62)_0%,rgba(9,32,23,0.28)_38%,rgba(9,32,23,0.72)_100%)]" data-id="element-544" data-mp="236xk2pyfk9id" />

            <div className="relative flex h-full flex-col justify-between p-6 sm:p-8 lg:p-10" data-id="element-545" data-mp="2vvmigh83q0qf">
              <div className="flex items-start justify-between gap-4" data-id="element-546" data-mp="2r2a2swleh49t">
                <Logo variant="stacked" height={64} className="brightness-0 invert" data-id="element-547" data-mp="2m51gqkx4sg3a" />
                <p className="hidden items-center gap-2 rounded-xl bg-white/12 px-3 py-2 text-caption leading-snug text-white backdrop-blur-md sm:flex" data-id="element-548" data-mp="3jjm6h9e13d1b">
                  <ShieldCheckIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-549" data-mp="3eruo3m510dm9" />
                  <span data-id="element-550" data-mp="2za4kvnjvicwd">{badge}</span>
                </p>
              </div>

              <div className="mt-8 max-w-md" data-id="element-551" data-mp="34as8dz8w8blu">
                <h2 className="text-[clamp(2rem,4.2vw,3.1rem)] font-semibold leading-[1.08] tracking-tight text-white" data-id="element-552" data-mp="34bk55de88fdp">
                  <span className="block" data-id="element-553" data-mp="2pfckkbywpbxw">{headline[0]}</span>
                  <span className="block" data-id="element-554" data-mp="2ucl4agzpdo19">{headline[1]}</span>
                  <span className="block text-brand-200" data-id="element-555" data-mp="3i141a0dayee1">{headline[2]}</span>
                </h2>
                <p className="mt-4 max-w-sm text-body text-white/80" data-id="element-556" data-mp="3n19w1pevch0c">{lead}</p>
              </div>

              {/* The four trust points, on the translucent panel from the design. */}
              <div className="mt-8 hidden rounded-2xl border border-white/15 bg-[rgba(9,42,30,0.55)] p-5 backdrop-blur-md sm:block" data-id="element-557" data-mp="3daxbhx720nu7">
                <ul className="grid gap-5 sm:grid-cols-2" data-id="element-558" data-mp="4u2lhibcuxxj">
                  {VALUES.map(value => <li key={value.title} className="flex items-start gap-3" data-id="element-559" data-mp="9mvn3xbj1q5e">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10" data-id="element-560" data-mp="1ubpsnyzgk13e">
                        <value.icon aria-hidden className="h-[18px] w-[18px] text-white" data-id="element-561" data-mp="1pcwbnwkypryh" />
                      </span>
                      <span className="min-w-0" data-id="element-562" data-mp="243vh4yqi40du">
                        <span className="block text-body-sm font-semibold text-white" data-id="element-563" data-mp="1z40p2ruzp4r7">
                          {value.title}
                        </span>
                        <span className="mt-0.5 block text-caption leading-snug text-white/75" data-id="element-564" data-mp="16ea9sfhmfwti">
                          {value.text}
                        </span>
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>

          {/* ------------------------------------------- authentication side */}
          <main className="flex flex-col bg-surface px-4 py-8 sm:px-8 lg:px-12 lg:py-10" data-id="element-565" data-mp="2vv307rhtru5j">
            {aside && <div className="mb-6 flex justify-end text-body-sm" data-id="element-566" data-mp="2rtzmbajc886y">{aside}</div>}
            <div className="mx-auto w-full max-w-[520px]" data-id="element-567" data-mp="1oghb4rwse0g3">{children}</div>
          </main>
        </div>

        {/* ------------------------------------------------- assurance strip */}
        <div className="border-t border-line bg-surface px-4 py-6 sm:px-8 lg:px-10" data-id="element-568" data-mp="1yfey3qekdlwz">
          <ul className="mx-auto grid max-w-[1200px] gap-6 md:grid-cols-3" data-id="element-569" data-mp="22f7c4imq8k9q">
            {ASSURANCES.map(item => <li key={item.title} className="flex items-start gap-3" data-id="element-570" data-mp="15s2p6j1btcm5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50" data-id="element-571" data-mp="1aqdwam08l90o">
                  <item.icon aria-hidden className="h-5 w-5 text-brand-700" data-id="element-572" data-mp="1eq4moolupn4o" />
                </span>
                <span className="min-w-0" data-id="element-573" data-mp="1jj7g8wqmowkv">
                  <span className="block text-body-sm font-semibold text-ink" data-id="element-574" data-mp="mzqaqxxeekvn">{item.title}</span>
                  <span className="mt-0.5 block text-caption leading-snug text-ink-muted" data-id="element-575" data-mp="r1bwz9vtobii">
                    {item.text}
                  </span>
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}